import { cva } from "class-variance-authority";
import { differenceInCalendarDays } from "date-fns";
import { CalendarSync, Plus, TriangleAlert, ZapOff } from "lucide-react";
import { useTranslation } from "react-i18next";

import { getCaptainBalanceState } from "../balance-state";
import type { CaptainBillingAccount } from "../types";
import { capResetDate } from "../utils";

import { Button } from "@/components/ui/v2/button";
import { formatDayMonth } from "@/utils/dateUtils";
import { useValueFormatter } from "@/utils/useValueFormatter";

const notice = cva("flex flex-col gap-4 rounded-xl border px-4 py-4 sm:flex-row sm:items-center sm:gap-5 sm:px-5", {
  variants: {
    state: {
      low: "border-(--badge-warning-border) bg-(--badge-warning-bg)",
      usedUp: "border-(--badge-error-border) bg-(--badge-error-bg)",
    },
  },
});

const noticeIcon = cva("grid size-11 shrink-0 place-items-center rounded-full border bg-background-alt", {
  variants: {
    state: {
      low: "border-(--badge-warning-border) text-(--badge-warning-text)",
      usedUp: "border-(--badge-error-border) text-(--badge-error-text)",
    },
  },
});

type CaptainBalanceBannerProps = {
  account: CaptainBillingAccount;
  onTopUp?: () => void;
};

export function CaptainBalanceBanner({ account, onTopUp }: CaptainBalanceBannerProps) {
  const { t, i18n } = useTranslation();
  const { format } = useValueFormatter();
  const state = getCaptainBalanceState(account);
  if (state === "healthy") return null;

  const role = onTopUp ? "admin" : "manager";
  const hasCap = account.monthlyCapTokens > 0;
  const resetDate = capResetDate(account.month);
  const values = {
    available: format(account.availableTokens, "decimal"),
    resetDate: formatDayMonth(resetDate, i18n.language),
  };
  const description =
    state === "low"
      ? t(`captainBilling.banner.low.${role}`, values)
      : t(`captainBilling.banner.usedUp.${role}.${hasCap ? "paused" : "noCap"}`, values);
  const Icon = state === "low" ? TriangleAlert : ZapOff;
  const days = Math.max(differenceInCalendarDays(resetDate, new Date()), 0);

  return (
    <section role="status" aria-live="polite" className={notice({ state })} data-testid="captain-balance-notice">
      <div className="flex min-w-0 flex-1 items-start gap-4">
        <span aria-hidden className={noticeIcon({ state })}>
          <Icon className="size-5" />
        </span>
        <div className="min-w-0 space-y-1 pt-0.5">
          <h2 className="text-base font-semibold text-foreground">{t(`captainBilling.banner.${state}.title`)}</h2>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 sm:shrink-0 sm:justify-end">
        {hasCap && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background-alt px-3 py-1 text-xs font-medium text-foreground tabular-nums">
            <CalendarSync className="size-3.5 text-muted-foreground" aria-hidden />
            {t("captainBilling.banner.refills", { date: values.resetDate, count: days })}
          </span>
        )}
        {onTopUp && (
          <Button onClick={onTopUp} className="gap-1.5">
            <Plus className="size-4" />
            {t("captainBilling.actions.topUp")}
          </Button>
        )}
      </div>
    </section>
  );
}
